import { useMemo, useState } from 'react'

import {
  AgChartOptions,
  AgScatterSeriesOptions,
  AgScatterSeriesTooltipRendererParams,
} from 'ag-charts-community'
import { parseISO } from 'date-fns'

import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DataTableFacetedFilter } from '@fortem-monorepo/react-ui'
import { RFQ } from '@fortem-monorepo/web-sdk'

import { sharedOptions, timeAxis } from '@/pages/flow/rfq/components/market-data-options'
import { useRFQDataQuery } from '@/react-query/queries/rfq'

type RFQItem = RFQ.GetRFQData[number]

const getUnderlyingsFromItem = (item: RFQItem) => {
  return (item?.combined_values ?? '')
    .split(',')
    .map((str) => str.trim())
    .sort()
}

export interface LevelsTableProps {
  options?: Omit<AgChartOptions, 'series' | 'axes'>
}

export const LevelsTable: React.FC<LevelsTableProps> = ({ options = {} }) => {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedBanks, setSelectedBanks] = useState<string[]>([])
  const [selectedUnderlyings, setSelectedUnderlyings] = useState<string[]>([])

  const levelsQuery = useRFQDataQuery()

  const underlyingPairs = useMemo(() => {
    return [
      ...new Set(
        (levelsQuery.data ?? []).map((item) => {
          return item.combined_values.split(', ').sort().join(', ')
        }),
      ),
    ].sort()
  }, [levelsQuery.data])

  const data = useMemo(() => {
    return (levelsQuery.data ?? [])
      .filter((item) => !isNaN(new Date(item.to_char).getTime()))
      .map((item) => ({
        date: parseISO(item.to_char),
        ...item,
      }))
      .filter((item) => item.level < 100)
  }, [levelsQuery.data])

  const {
    products,
    underlyings: _underlyings,
    banks,
    types,
  } = useMemo(() => {
    return {
      products: [...new Set(data.map((item) => item.producttype))],
      underlyings: [
        ...new Set(
          data
            .flatMap((item) => item.combined_values)
            .map((str) =>
              (str ?? '')
                .split(',')
                .map((str) => str.trim())
                .sort()
                .join(', '),
            )
            .filter((val) => val !== null),
        ),
      ].sort(),
      banks: [...new Set(data.map((item) => item.dealer_id))].filter((v) => v),
      types: [...new Set(data.map((item) => item.dropornot))],
    }
  }, [data])

  const selectedData = useMemo(() => {
    return data.filter((item) => {
      const itemUnderlyings = getUnderlyingsFromItem(item)

      const checks = [
        // Products
        selectedProducts.length === 0 || selectedProducts.includes(item.producttype),

        // Drop/Types
        selectedTypes.length === 0 || selectedTypes.includes(item.dropornot),

        // Banks
        selectedBanks.length === 0 || selectedBanks.includes(item.dealer_id),

        // Underlyings
        selectedUnderlyings.length === 0 ||
          (selectedUnderlyings.length === itemUnderlyings.length &&
            selectedUnderlyings.every((underlying) => itemUnderlyings.includes(underlying))),
      ]

      return checks.every(Boolean)
    })
  }, [data, selectedBanks, selectedProducts, selectedTypes, selectedUnderlyings])

  return (
    <Widget>
      <WidgetHeaderWithQuery title={'Pricing Data'} query={levelsQuery}>
        <div className="flex items-center gap-2 px-3 py-2 border-t">
          <DataTableFacetedFilter
            size={'sm'}
            title={'Products'}
            values={selectedProducts}
            setValues={setSelectedProducts}
            options={products.map((product) => ({ label: product, value: product }))}
          />
          <DataTableFacetedFilter
            size={'sm'}
            title={'Drop'}
            values={selectedTypes}
            setValues={setSelectedTypes}
            options={types.map((product) => ({ label: product, value: product }))}
          />
          <DataTableFacetedFilter
            size={'sm'}
            title={'Banks'}
            values={selectedBanks}
            setValues={setSelectedBanks}
            options={banks.map((product) => ({ label: product, value: product }))}
          />
          <DataTableFacetedFilter
            size={'sm'}
            title={'Underlyings'}
            values={selectedUnderlyings}
            setValues={setSelectedUnderlyings}
            // options={underlyings.map((product) => ({ label: product, value: product }))}
            options={underlyingPairs.map((product) => ({ label: product, value: product }))}
          />
        </div>
      </WidgetHeaderWithQuery>
      <WidgetContent>
        <CustomAgChart
          options={{
            ...sharedOptions,
            title: {
              text: `${selectedBanks.join(', ')} ${selectedProducts.join(', ')} ${selectedTypes.join(', ')} ${selectedUnderlyings.join(', ')}`,
            },
            series: [
              ...selectedBanks.map((bank) => {
                return {
                  data: selectedData.filter((item) => item.dealer_id.includes(bank)),
                  type: 'scatter',
                  yKey: 'level',
                  xKey: 'date',
                  yName: bank,
                  tooltip: {
                    renderer: (
                      params: AgScatterSeriesTooltipRendererParams<(typeof data)[number]>,
                    ) => {
                      return {
                        data: [
                          { label: bank, value: params.datum.level.toFixed(2) },
                          { label: 'Product', value: params.datum.producttype },
                          { label: 'Drop', value: params.datum.dropornot },
                          { label: 'Underlyings', value: params.datum.combined_values },
                        ],
                      }
                    },
                  },
                } satisfies AgScatterSeriesOptions
              }),
            ],
            axes: [
              timeAxis,
              {
                type: 'number',
                position: 'left',
                crosshair: {
                  enabled: true,
                },
                keys: ['level'],
              },
            ],
            ...options,
          }}
        />
      </WidgetContent>
    </Widget>
  )
}
